import React, { useState } from 'react';
import { Send, Trash2, User, Mail, AlertTriangle, CheckCircle, RotateCcw, Shield } from 'lucide-react';
import { contactAPI } from '../../../api/contact';
import { supabase } from '../../../lib/supabase';
import { log } from '../../../utils/logger';
import toast from 'react-hot-toast';

const DeleteAccountPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    reason: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Check if user is authenticated
  React.useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);

      // Pre-fill form for authenticated users
      if (user) {
        setFormData(prev => ({
          ...prev,
          name: `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || '',
          email: user.email || ''
        }));
      }
    };

    checkUser();

    // Load reCAPTCHA for anonymous users
    if (!window.grecaptcha) {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${import.meta.env.VITE_RECAPTCHA_SITE_KEY}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      return () => {
        // Cleanup script on unmount
        document.head.removeChild(script);
      };
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewRequest = () => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      reason: '',
      additionalInfo: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.reason.trim()) {
      log.warn('Delete account form validation failed - missing required fields');
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare submit data
      const submitData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        reason: formData.reason.trim(),
        additionalInfo: formData.additionalInfo.trim()
      };

      let result;

      if (currentUser) {
        // Authenticated user - no reCAPTCHA required
        log.info('🔄 Submitting deletion request as authenticated user');
        result = await contactAPI.submitDeletionRequest(submitData);
      } else {
        // Anonymous user - require reCAPTCHA
        log.info('🔍 Anonymous user detected - executing reCAPTCHA verification');

        // Execute reCAPTCHA verification for anonymous users
        if (!window.grecaptcha || !window.grecaptcha.enterprise) {
          throw new Error('reCAPTCHA is not available. Please refresh the page and try again.');
        }

        const token = await window.grecaptcha.enterprise.execute(import.meta.env.VITE_RECAPTCHA_SITE_KEY, {
          action: 'DELETE_ACCOUNT_REQUEST'
        });

        if (!token) {
          throw new Error('reCAPTCHA verification failed. Please try again.');
        }

        log.info('reCAPTCHA token generated successfully');
        log.info('🔄 Submitting deletion request as anonymous user');

        result = await contactAPI.submitDeletionRequestAnonymous({
          ...submitData,
          recaptchaToken: token
        });
      }

      const { error, success } = result;

      if (!success) {
        throw error || new Error('Failed to submit account deletion request');
      }

      log.info('✅ Account deletion request submitted successfully');

      // Show success state
      setIsSubmitted(true);
      toast.success('Your account deletion request has been submitted successfully.');
    } catch (error) {
      log.error('❌ Account deletion request failed:', error);
      toast.error(error.message || 'Failed to submit deletion request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img
                src="/TeamRezyLogo.png"
                alt="TeamRezy Logo"
                className="h-16 w-auto object-contain"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex items-center mb-6">
            <Trash2 className="w-8 h-8 mr-3 text-red-600" />
            <h1 className="text-3xl font-bold text-gray-900">Request Account Deletion</h1>
          </div>

          {!isSubmitted ? (
            <>
              {/* Important Notice */}
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div className="flex items-start">
                  <AlertTriangle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-900 mb-2">Important Information</h3>
                    <ul className="text-sm text-red-800 space-y-1 list-disc list-inside">
                      <li>Account deletion is permanent and cannot be undone</li>
                      <li>All your personal information will be permanently deleted within 30 days</li>
                      <li>You will lose access to all reservations, history, and account data</li>
                      <li>We will send a confirmation email before processing your request</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* What Gets Deleted Section */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <div className="flex items-start">
                  <Shield className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">What Will Be Deleted</h3>
                    <div className="text-sm text-blue-800 space-y-2">
                      <div>
                        <p className="font-medium">Permanently Deleted:</p>
                        <ul className="list-disc list-inside ml-4">
                          <li>Your name, email, phone number, and profile information</li>
                          <li>Login credentials and authentication data</li>
                          <li>Location data and preferences</li>
                          <li>Personal communications and messages</li>
                        </ul>
                      </div>
                      <div className="mt-2">
                        <p className="font-medium">Anonymized & Retained (Legal Requirement):</p>
                        <ul className="list-disc list-inside ml-4">
                          <li>Reservation history (anonymized as "Deleted User")</li>
                          <li>Payment records (required for 7 years for tax/audit purposes)</li>
                          <li>Aggregated analytics data (no personal identification)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Deletion Request Form */}
              <div className="mb-6">
                <p className="text-gray-700 mb-6">
                  {currentUser ? (
                    <>We're sorry to see you go, {currentUser.user_metadata?.first_name || 'there'}. Please complete this form to request deletion of your account and all associated personal data.</>
                  ) : (
                    <>To request deletion of your TeamRezy account and associated personal data, please complete the form below. We will verify your identity and process your request within 30 days as required by GDPR and CCPA regulations.</>
                  )}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="w-4 h-4 inline mr-1" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="your.email@example.com"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      This must be the email associated with your TeamRezy account
                    </p>
                  </div>

                  <div>
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                      Reason for Deletion *
                    </label>
                    <select
                      id="reason"
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      required
                    >
                      <option value="">Please select a reason</option>
                      <option value="no_longer_need">No longer need the service</option>
                      <option value="privacy_concerns">Privacy concerns</option>
                      <option value="switching_service">Switching to another service</option>
                      <option value="too_many_emails">Too many emails/notifications</option>
                      <option value="service_issues">Issues with the service</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Information (Optional)
                    </label>
                    <textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Please provide any additional details or feedback that could help us improve..."
                    />
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="confirm"
                        required
                        className="mt-1 mr-3 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                      <label htmlFor="confirm" className="text-sm text-gray-700">
                        <strong>I understand</strong> that this action is permanent and irreversible. All my personal data will be permanently deleted within 30 days, and I will lose access to all reservations and account history.
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 text-white px-4 py-3 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors font-medium"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Submitting Request...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Submit Deletion Request
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    <strong>Response Time:</strong> We will process your request within 30 days as required by law.
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    Questions? Contact us at <a href="mailto:privacy@teamrezy.com" className="text-navy-600 hover:underline">privacy@teamrezy.com</a>
                  </p>
                  {!currentUser && (
                    <p className="text-xs text-gray-500 mt-3">
                      This site is protected by reCAPTCHA and the Google{' '}
                      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-navy-600 hover:underline">
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="text-navy-600 hover:underline">
                        Terms of Service
                      </a>{' '}
                      apply.
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Request Submitted Successfully</h2>
              <p className="text-gray-600 mb-6">
                {currentUser ? (
                  <>Thank you, {currentUser.user_metadata?.first_name || 'there'}. We've received your account deletion request for {currentUser.email}.</>
                ) : (
                  <>Thank you. We've received your account deletion request.</>
                )}
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left">
                <p className="text-sm font-medium text-blue-900 mb-2">What happens next?</p>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>You will receive a confirmation email within 24 hours</li>
                  <li>We will verify your identity to ensure account security</li>
                  <li>Your personal data will be permanently deleted within 30 days</li>
                  <li>You will receive a final confirmation email once deletion is complete</li>
                </ul>
              </div>
              <div className="space-y-2 text-sm text-gray-600 mb-6">
                <p className="font-medium">Need to cancel this request?</p>
                <p>If you change your mind, reply to the confirmation email within 7 days, or contact us at:</p>
                <p><a href="mailto:privacy@teamrezy.com" className="text-navy-600 hover:underline font-medium">privacy@teamrezy.com</a></p>
              </div>
              <button
                onClick={handleNewRequest}
                className="bg-navy-600 text-white px-6 py-2 rounded-md hover:bg-navy-800 transition-colors flex items-center mx-auto"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Submit Another Request
              </button>
            </div>
          )}
        </div>

        {/* Additional Resources */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Privacy Rights</h3>
            <p className="text-sm text-gray-600 mb-4">
              Under GDPR and CCPA, you have the right to request deletion of your personal data. We take these rights seriously and will process your request within the legally required timeframe.
            </p>
            <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" className="text-navy-600 hover:underline text-sm font-medium">
              Read our Privacy Policy →
            </a>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Need Help?</h3>
            <p className="text-sm text-gray-600 mb-4">
              If you're having issues with our service, we'd love to help before you go. Our support team is available to address any concerns.
            </p>
            <a href="/contact" className="text-navy-600 hover:underline text-sm font-medium">
              Contact Support →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;